/**
 * Custom hook for managing the state and logic of the change phone flow.
 *
 * @returns {Object} The state and functions for managing the change phone flow.
 * @returns {number} step - The current step in the change phone flow.
 * @returns {ChangePhoneData} data - The data associated with the change phone flow.
 * @returns {React.Dispatch<Partial<ChangePhoneData>>} dispatch - The dispatch function for updating the state.
 * @returns {Function} t - Translation function from the i18next library.
 * @returns {string} nationalIdDescription - The description of the national ID.
 * @returns {Function} handleForward - Function to move to the next step in the flow.
 * @returns {string} email - The email of the customer.
 * @returns {string} customerId - The ID of the customer.
 * @returns {Function} handleErrorCode - Function to handle error codes.
 */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWizardContext, WizardFlowActionType } from './WizardContext';
import { useStore } from '../../../store';
import { ApiError, ErrorData } from '../ChangePhone';
import { ChangePhoneFlow } from '../ChangePhoneSteps';

export enum errorCodesEnum {
  tookBreak = 'tookBreak',
  takeFive = 'takeFive',
  invalidCode = 'invalidCode',
  sameNumberAsOldOne = 'sameNumberAsOldOne',
  notSupportedNumber = 'notSupportedNumber',
  mobilePhoneNumber = 'mobilePhoneNumber',
  problemWithPhone = 'problemWithPhone',
  invalidPhone = 'invalidPhone',
  default = 'default',
}
export const useStepHook = () => {
  const { t } = useTranslation();
  const { customerId } = useStore((state) => state.user);
  const [inputData, setInputData] = React.useState<string>('');
  const { step, data, dispatch, lastStep, isPhoneValid } = useWizardContext();
  const summary = useStore((state) => state.summary);
  const {
    customer: { nationalIdDescription, email },
  } = summary;

  const INLINE_ERROR_CODES = [40032, 40051, 4010];

  type InlineErrorCode = (typeof INLINE_ERROR_CODES)[number];

  // typeguard for inline error code
  const isInlineErrorCode = (code: number): code is InlineErrorCode =>
    INLINE_ERROR_CODES.includes(code as InlineErrorCode);

  const ERROR_CODES = [
    40043, 4010, 40044, 4079, 4026, 40032, 4350, 4352, 4009, 4010, 40043, 40044, 4079, 40051, 4302, 4304, 9999,
  ];

  // typeguard for error code
  type ErrorCode = (typeof ERROR_CODES)[number];
  const isErrorCode = (code: number): code is ErrorCode => ERROR_CODES.includes(code);

  const errorMapping = new Map<number, errorCodesEnum>([
    [40043, errorCodesEnum.tookBreak],
    [40044, errorCodesEnum.tookBreak],
    [4079, errorCodesEnum.tookBreak],
    [4026, errorCodesEnum.takeFive],
    [40032, errorCodesEnum.invalidCode],
    [40051, errorCodesEnum.sameNumberAsOldOne],
    [4302, errorCodesEnum.notSupportedNumber],
    [4304, errorCodesEnum.mobilePhoneNumber],
    [4350, errorCodesEnum.problemWithPhone],
    [4352, errorCodesEnum.problemWithPhone],
    [4009, errorCodesEnum.problemWithPhone],
    [4010, errorCodesEnum.invalidPhone],
    [9999, errorCodesEnum.default],
  ]);

  const getErrorKeyByCode = (code: string): errorCodesEnum =>
    errorMapping.get(parseInt(code, 10)) || errorCodesEnum.default;

  const handleSetError = (error: ErrorData) => {
    if (!error.errorCode) return;
    dispatch({
      type: WizardFlowActionType.SET_DATA,
      payload: { error },
    });
    dispatch({
      type: WizardFlowActionType.LAST_STEP,
      payload: step,
    });
    dispatch({
      type: WizardFlowActionType.SET_STEP,
      payload: ChangePhoneFlow.ERROR,
    });
  };

  const handleInlineError = (errorKey: errorCodesEnum, errorCode: number) => {
    dispatch({
      type: WizardFlowActionType.SET_DATA,
      payload: {
        error: {
          errorKey,
          errorCode,
          inlineError: true,
          errorMessage: t(`CHANGE_PHONE.ERRORS.${errorKey}.description`),
        },
      },
    });
  };

  const handleErrorCode = (error: ApiError) => {
    const errorCode = error.code;
    const { localizedMessage } = error;
    const errorKey = getErrorKeyByCode(errorCode.toString());
    if (isInlineErrorCode(errorCode)) {
      handleInlineError(errorKey, errorCode);
    } else if (isErrorCode(errorCode)) {
      handleSetError({
        errorCode,
        errorKey,
        inlineError: false,
        prevStep: step,
        localizedMessage,
      });
    } else {
      handleSetError({
        errorCode: 9999,
        errorKey,
        inlineError: false,
        prevStep: step,
        localizedMessage,
      });
    }
  };

  const handleForward = () => {
    dispatch({ type: WizardFlowActionType.NEXT_STEP });
  };

  const handleOnInputChange = useCallback(
    (value: string) => {
      setInputData(value);
      if (data.error) {
        dispatch({
          type: WizardFlowActionType.SET_DATA,
          payload: { error: undefined },
        });
      }
    },
    [data.error],
  );

  return {
    step,
    data,
    t,
    dispatch,
    nationalIdDescription,
    handleForward,
    email,
    customerId,
    handleErrorCode,
    handleOnInputChange,
    inputData,
    lastStep,
    isPhoneValid,
  };
};
